import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useCommandStore } from "@/lib/command-registry";
import { formatShortcut } from "@/lib/shortcuts";
/**
 * Keyboard shortcuts dialog (shift+/). Walks the command registry and groups
 * by shortcut, so anything with a `shortcut` property shows up automatically.
 */
import { useMemo } from "react";

export function KeyboardShortcutsDialog(): JSX.Element {
  const open = useCommandStore((s) => s.shortcutsOpen);
  const closeShortcuts = useCommandStore((s) => s.closeShortcuts);
  const commandsMap = useCommandStore((s) => s.commands);

  const shortcuts = useMemo(() => {
    return Array.from(commandsMap.values())
      .filter((c) => c.shortcut && c.shortcut.length > 0)
      .map((c) => ({
        id: c.id,
        title: c.title,
        group: c.group ?? "Other",
        // biome-ignore lint/style/noNonNullAssertion: filtered above so c.shortcut is defined
        keys: formatShortcut(c.shortcut!),
      }));
  }, [commandsMap]);

  const grouped = useMemo(() => {
    const m = new Map<string, typeof shortcuts>();
    for (const s of shortcuts) {
      const bucket = m.get(s.group);
      if (bucket) bucket.push(s);
      else m.set(s.group, [s]);
    }
    return Array.from(m.entries());
  }, [shortcuts]);

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : closeShortcuts())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Press{" "}
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-xs">Esc</kbd>{" "}
            to close.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {grouped.length === 0 ? (
            <p className="text-sm text-muted-foreground">No shortcuts registered yet.</p>
          ) : (
            grouped.map(([group, items]) => (
              <div key={group}>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {group}
                </h3>
                <ul className="space-y-1.5">
                  {items.map((s) => (
                    <li key={s.id} className="flex items-center justify-between text-sm">
                      <span>{s.title}</span>
                      <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-xs font-mono">
                        {s.keys}
                      </kbd>
                    </li>
                  ))}
                </ul>
                <Separator className="mt-3" />
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
